import React from "react"
import { useNavigate } from "@tanstack/react-router"
import { z } from "zod"

import {
	Button,
	Dialog,
	Form,
	FormInput,
	FormSelect,
	FormTextarea,
	Loader,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useSession } from "@/lib/session"
import { useZeroWorkspace } from "@/lib/zero"

interface IProps {
	open: boolean
	onClose: () => void
}

const cancellationReasons = [
	{ label: "Some features are missing", value: "Some features are missing" },
	{
		label: "Too hard or complex to use",
		value: "Too hard or complex to use",
	},
	{
		label: "Too many performance issues or bugs",
		value: "Too many performance issues or bugs",
	},
	{
		label: "Customer service was less than expected",
		value: "Customer service was less than expected",
	},
	{ label: "It's too expensive", value: "It's too expensive" },
	{
		label: "We don't need it anymore",
		value: "We don't need it anymore",
	},
	{ label: "Other", value: "Other" },
]

const deleteFormSchema = z.object({
	reason: z.string().min(1, "Please select a reason"),
	elaboration: z
		.string()
		.min(1, "Please provide some details, it helps us improve!"),
	competitor: z.string().min(1, "Your input here would really help us."),
	workspaceName: z.string().min(1, "Please type your workspace name"),
})

export const DeleteWorkspaceModal = React.memo(({ open, onClose }: IProps) => {
	const { session } = useSession()
	const workspace = useZeroWorkspace()
	const workspaceName = workspace?.name ?? session?.workspaceName ?? ""

	const hasSubscription = !!workspace?.stripeSubscriptionId

	const { data: trialInfo } = api.workspace.trialInfo.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const onStartupProgram = trialInfo?.isStartupProgram === true

	const form = useZodForm({
		schema: deleteFormSchema,
	})

	const reasonValue = form.watch("reason")

	const navigate = useNavigate()

	const cancelSubscription = api.workspace.cancelSubscription.useMutation()

	const deleteWorkspace = api.workspace.delete.useMutation({
		onSuccess: () => {
			void navigate({ to: "/logout" })
		},
		onError(error) {
			toast.error({
				title: "Something went wrong",
				description: error.message,
			})
		},
	})

	const isLoading = deleteWorkspace.isLoading || cancelSubscription.isLoading

	const onSubmit = async (values: z.infer<typeof deleteFormSchema>) => {
		if (!session) return

		if (!["ADMIN"].includes(session.membershipRole)) {
			toast.error({
				title: "Permission denied",
				description: "Only workspace admins can delete the workspace.",
			})
			return
		}

		if (values.workspaceName !== workspaceName) {
			toast.error({
				title: "Workspace name doesn't match",
				description: `Please type "${workspaceName}" exactly to confirm deletion.`,
			})
			return
		}

		try {
			await cancelSubscription.mutateAsync({
				reason: values.reason,
				elaboration: values.elaboration,
				competitor: values.competitor,
				isWorkspaceDeletion: true,
			})
		} catch {
			// Don't block deletion if feedback/cancellation fails
		}

		deleteWorkspace.mutate({
			workspaceId: session.workspaceId,
			workspaceName,
		})
	}

	return (
		<Dialog
			onOpenChange={(value) => {
				if (!value) {
					onClose()
				}
			}}
			isOpen={open}
			title="Delete workspace"
			description={
				<>
					You&lsquo;re about to delete your workspace{" "}
					<b className="underline">{workspaceName}</b>. This cannot be reversed
					once confirmed. Please type the name of your workspace to confirm.
				</>
			}
			footer={
				<div className="flex w-full items-center justify-end gap-2">
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="danger"
						form="delete-workspace-form"
						type="submit"
						disabled={isLoading || onStartupProgram}
					>
						{hasSubscription
							? "Delete workspace and cancel subscription"
							: "Delete workspace"}
					</Button>
				</div>
			}
		>
			{isLoading && (
				<div className="absolute inset-0 z-50 flex items-center justify-center gap-4 rounded-lg backdrop-blur">
					<Loader />
					<span className="pl-2">Deleting workspace</span>
				</div>
			)}

			{onStartupProgram ? (
				<div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-900 dark:border-yellow-700/60 dark:bg-yellow-950/40 dark:text-yellow-200">
					This workspace is on the Startup program (12-month contract). Contact
					us to cancel before deleting the workspace.
				</div>
			) : (
				<Form
					id="delete-workspace-form"
					form={form}
					onSubmit={onSubmit}
					className="space-y-3"
				>
					<FormSelect
						name="reason"
						placeholder="Why are you leaving Productlane?"
						options={cancellationReasons}
						className="mt-2"
						selectClassName={!reasonValue ? "text-tertiary" : undefined}
					/>
					<FormTextarea
						name="elaboration"
						placeholder="Can you elaborate? How could we have improved?"
						minRows={3}
					/>
					<FormInput
						name="competitor"
						placeholder="What tool are you switching to?"
					/>
					<FormInput
						name="workspaceName"
						label="Type your workspace name to confirm"
						placeholder={workspaceName}
						className="mt-2"
					/>
				</Form>
			)}
		</Dialog>
	)
})
